import {
  MENTAL_PATTERN_ORDER,
  UNDER_STRESS_EMOTIONS
} from "../constants/mentalPatterns.js";
import { getReportingEmotionLabel } from "./confirmedEmotion.js";

const PATTERN_SIGNALS = {
  overthinking: [
    /\b(overthink|ruminat|spiral|looping|can't stop thinking|keeps? running through|what if|going over|replay(?:ing)?|analyz(?:e|ing)|second[- ]?guess|stuck in my head)\b/,
    /\b(mind (?:won't|doesn't) (?:shut|stop)|thoughts (?:won't|keep) (?:stop|coming))\b/
  ],
  shutting_down: [
    /\b(shut(?:ting)? down|shut down|freeze|frozen|can't move|went blank|blanked out|dissociat|checked out|collapse|collapsed)\b/,
    /\b(no energy to (?:speak|talk|respond)|can't get (?:out of bed|up)|paralyz)\b/
  ],
  people_pleasing: [
    /\b(people[- ]?pleas|please everyone|can't say no|said yes (?:when|but)|afraid to disappoint|don't want to let (?:them|anyone) down|keep agreeing|put (?:others|everyone) first)\b/,
    /\b(need them to like me|approval|walk on eggshells)\b/
  ],
  staying_busy: [
    /\b(stay(?:ing)? busy|kept busy|packed schedule|no time to (?:feel|think)|distract(?:ed|ing)? myself|keep moving|always doing|fill(?:ing)? (?:every|all) (?:minute|moment)|avoid(?:ing)? (?:feeling|feelings))\b/,
    /\b(work(?:ing)? non[- ]?stop|can't sit still|restless productivity)\b/
  ],
  pulling_away: [
    /\b(pull(?:ing)? away|withdraw|withdrew|isolat(?:e|ing|ed)|ghost(?:ing|ed)?|distance myself|push(?:ing)? (?:them|people) away|avoid(?:ing)? (?:them|people|contact)|shut (?:them|people) out)\b/,
    /\b(don't want to see anyone|left the group|unplugged from everyone)\b/
  ],
  irritability: [
    /\b(irritab|snappy|short fuse|on edge|snapped at|lashed out|quick to anger|little things (?:set|make) me|grumpy|cranky|frustrated with everyone)\b/,
    /\b(anger (?:at|over) (?:small|little)|blow up over nothing)\b/
  ],
  numbness: [
    /\b(numb|numbness|feel nothing|don't feel anything|empty inside|flat affect|emotionally flat|disconnected from (?:my )?feelings|void|hollow)\b/,
    /\b(nothing lands|can't cry|can't feel)\b/
  ],
  hyper_independence: [
    /\b(do (?:it|everything) (?:myself|alone)|on my own|don't need (?:anyone|help)|won't ask for help|can't ask for help|handle (?:it|everything) alone|self[- ]?reliant|hyper[- ]?independen)\b/,
    /\b(strong enough alone|don't want to burden|refuse(?:d)? (?:help|support)|carry (?:it|everything) myself)\b/
  ]
};

const STRESS_TEXT_SIGNALS =
  /\b(stress(?:ed|ful)?|under pressure|overwhelm(?:ed|ing)?|burn(?:ed|t)? out|burnout|exhaust(?:ed|ion)|can't cope|too much (?:on|to)|breaking point|frazzled|tense|tension|drained|depleted|at my limit|maxed out|on edge)\b/i;

function normalizeText(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function getInsightCombinedText(insight) {
  const parts = [];
  if (insight?.entry_text) parts.push(insight.entry_text);
  const j = insight?.insight_json;
  if (j && typeof j === "object") {
    const fields = [
      // Deep Reflection / legacy EIER
      "event_reflection",
      "interpretation_reflection",
      "evidence_reflection",
      "response_statement",
      "energy",
      "issues",
      "unconscious_thoughts",
      "reflect",
      // Quick Clarity
      "understanding",
      "user_interpretation",
      "primary_insight",
      "clarifying_question"
    ];
    for (const key of fields) {
      const v = j[key];
      if (typeof v === "string" && v.trim()) parts.push(v);
      if (Array.isArray(v)) parts.push(v.filter((x) => typeof x === "string").join(" "));
    }
    const factArrays = ["observed_or_reported_facts", "unknowns"];
    for (const key of factArrays) {
      const v = j[key];
      if (Array.isArray(v)) parts.push(v.filter((x) => typeof x === "string").join(" "));
    }
  }
  return normalizeText(parts.join(" "));
}

export function getInsightEmotion(insight) {
  const reported = getReportingEmotionLabel(insight);
  if (reported) return reported;
  return "";
}

export function isUnderStressInsight(insight) {
  const emotion = getInsightEmotion(insight);
  if (UNDER_STRESS_EMOTIONS.has(emotion)) return true;
  const text = getInsightCombinedText(insight);
  return STRESS_TEXT_SIGNALS.test(text);
}

function emotionToPatternFallback(emotion) {
  const map = {
    Overwhelmed: "shutting_down",
    Anxious: "overthinking",
    Frustrated: "irritability",
    Angry: "irritability",
    Fearful: "pulling_away",
    Confused: "overthinking",
    Skeptical: "overthinking",
    Insecure: "people_pleasing",
    Lonely: "pulling_away",
    Hostile: "irritability",
    Hurt: "pulling_away",
    Ashamed: "pulling_away",
    Guilty: "people_pleasing",
    Sad: "numbness",
    Indifferent: "numbness",
    Stressed: "shutting_down",
    Rage: "irritability",
    Furious: "irritability"
  };
  return map[emotion] || "overthinking";
}

export function classifyMentalPattern(insight) {
  const text = getInsightCombinedText(insight);
  if (!text) return emotionToPatternFallback(getInsightEmotion(insight));

  const scores = {};
  for (const key of MENTAL_PATTERN_ORDER) {
    const patterns = PATTERN_SIGNALS[key] || [];
    let score = 0;
    for (const re of patterns) {
      if (re.test(text)) score += 2;
    }
    if (score > 0) scores[key] = score;
  }

  const keys = Object.keys(scores);
  if (!keys.length) return emotionToPatternFallback(getInsightEmotion(insight));

  keys.sort((a, b) => scores[b] - scores[a] || MENTAL_PATTERN_ORDER.indexOf(a) - MENTAL_PATTERN_ORDER.indexOf(b));
  return keys[0];
}

function parseInsightDate(insight) {
  const raw = insight.created_at != null ? insight.created_at : insight.createdAt;
  const d = raw ? new Date(raw) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function toWeekKey(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + mondayOffset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dayNum = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dayNum}`;
}

function formatWeekLabel(weekKey) {
  const d = new Date(weekKey + "T12:00:00");
  if (Number.isNaN(d.getTime())) return weekKey;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const RECENCY_DAYS = 14;
const RECENCY_BOOST = 3;
const MID_DAYS = 30;
const MID_BOOST = 1.5;

export function analyzeMentalPatterns(insights) {
  const list = Array.isArray(insights) ? insights : [];
  const now = Date.now();
  const msDay = 86400000;

  const stressInsights = [];
  const distribution = MENTAL_PATTERN_ORDER.reduce((acc, k) => {
    acc[k] = 0;
    return acc;
  }, {});
  const weightedScores = { ...distribution };
  let latestPattern = null;
  let latestDate = 0;

  for (const item of list) {
    if (!isUnderStressInsight(item)) continue;

    const date = parseInsightDate(item);
    const pattern = classifyMentalPattern(item);
    stressInsights.push({
      id: item.id,
      date: date.toISOString(),
      pattern,
      emotion: getInsightEmotion(item),
      preview: (item.entry_text || "").slice(0, 120)
    });

    if (pattern) {
      distribution[pattern] = (distribution[pattern] || 0) + 1;
      const ageDays = (now - date.getTime()) / msDay;
      let weight = 1;
      if (ageDays <= RECENCY_DAYS) weight = RECENCY_BOOST;
      else if (ageDays <= MID_DAYS) weight = MID_BOOST;
      weightedScores[pattern] = (weightedScores[pattern] || 0) + weight;

      if (date.getTime() >= latestDate) {
        latestDate = date.getTime();
        latestPattern = pattern;
      }
    }
  }

  stressInsights.sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalStress = stressInsights.length;

  const weekMap = new Map();
  for (const entry of stressInsights) {
    const wk = toWeekKey(entry.date);
    if (!weekMap.has(wk)) {
      weekMap.set(wk, {
        weekKey: wk,
        count: 0,
        patterns: MENTAL_PATTERN_ORDER.reduce((a, k) => ({ ...a, [k]: 0 }), {})
      });
    }
    const bucket = weekMap.get(wk);
    bucket.count += 1;
    if (entry.pattern) bucket.patterns[entry.pattern] = (bucket.patterns[entry.pattern] || 0) + 1;
  }

  const weeklyTrend = Array.from(weekMap.values())
    .sort((a, b) => a.weekKey.localeCompare(b.weekKey))
    .slice(-10)
    .map((w) => ({ ...w, label: formatWeekLabel(w.weekKey) }));

  const maxWeekly = weeklyTrend.reduce((m, w) => Math.max(m, w.count), 0) || 1;

  let attentionPattern = null;
  const scoredPatterns = MENTAL_PATTERN_ORDER.filter((k) => weightedScores[k] > 0);
  if (scoredPatterns.length) {
    scoredPatterns.sort((a, b) => {
      const diff = weightedScores[b] - weightedScores[a];
      if (diff !== 0) return diff;
      if (latestPattern === a) return -1;
      if (latestPattern === b) return 1;
      return MENTAL_PATTERN_ORDER.indexOf(a) - MENTAL_PATTERN_ORDER.indexOf(b);
    });
    attentionPattern = scoredPatterns[0];
  }

  const patternsWithData = MENTAL_PATTERN_ORDER.filter((k) => distribution[k] > 0);

  return {
    stressInsights,
    distribution,
    weightedScores,
    totalStress,
    totalReflections: list.length,
    weeklyTrend,
    maxWeekly,
    attentionPattern,
    latestPattern,
    patternsWithData
  };
}
